import React, { useEffect, useState } from "react";
import useForm from "../../../../hooks/useForm";
import Button from "../../../form/Button";
import { useMutation, useQuery } from "@tanstack/react-query";
import Input from "../../../form/Input";
import DatePicker from "../../../form/DatePicker";
import { useNavigate, useParams } from "react-router-dom";
import FlexibleSelect from "../../../form/select/FlexibleSelect";
import { Check } from "lucide-react";
import { quizAddEdit, quizDetails } from "../../../../services/quizService";

const validation = {
  title: { required: true, message: "Title is required" },
};

const ManageSpinWheel = ({ getResponse = () => {}, close = () => {} }) => {
  const params = useParams();
  const navigate = useNavigate();
  const id = params?.id;
  const { values, handleChange, handleSubmit, errors, setFieldsValue, formValidate } = useForm({
    validationRules: validation,
    initialState: { status: "active" },
  });
  const [submitted, setSubmitted] = useState(false);
  const [options, setOptions] = useState([
    { id: crypto.randomUUID(), label: { text: "" }, points: "0", isCorrect: false },
    { id: crypto.randomUUID(), label: { text: "" }, points: "0", isCorrect: false },
    { id: crypto.randomUUID(), label: { text: "" }, points: "0", isCorrect: false },
    { id: crypto.randomUUID(), label: { text: "" }, points: "0", isCorrect: false },
    { id: crypto.randomUUID(), label: { text: "" }, points: "0", isCorrect: false },
    { id: crypto.randomUUID(), label: { text: "" }, points: "0", isCorrect: false },
    { id: crypto.randomUUID(), label: { text: "" }, points: "0", isCorrect: false },
    { id: crypto.randomUUID(), label: { text: "" }, points: "0", isCorrect: false },
  ]);

  const { data: details } = useQuery({
    queryKey: ["wheel-details", id],
    queryFn: async () => {
      const res = await quizDetails({ id: id });
      return res?.data;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (details?._id) {
      setFieldsValue({
        ...details,
        publishStartDate: details?.schedule?.startDate,
        publishEndDate: details?.schedule?.endDate,
        title: details?.label?.text,
      });

      if (details?.options?.length) {
        const normalizedOptions = details.options.map((opt) => ({
          id: opt?.id || crypto.randomUUID(),
          label: { text: opt?.label?.text || "" },
          points: opt?.points,
          isCorrect: !!opt?.isCorrect,
        }));
        setOptions(normalizedOptions);
      }
    }
  }, [details]);

  const {
    mutate,
    error,
    isPending: isLoading,
  } = useMutation({
    mutationFn: async (val) => {
      return await quizAddEdit({
        label: {
          text: val?.title,
          // image: coverMedia?._id,
        },
        status: val?.status,
        type: "radio",
        publishStartDate: val?.publishStartDate,
        publishEndDate: val?.publishEndDate,
        timeLimit:0,
        sectionType: "wheel",
        options: options.map((opt) => ({
          ...opt,
          points: Number(opt.points),
        })),
        ...(details?._id && { id: details._id }),
      });
    },

    onSuccess: (res) => {
      getResponse(res);
      if (res?.status === 200 || res?.status === 201) {
        navigate(`/admin/spin-wheel`);
      }
      return res;
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const handleOptionChange = (id, value) => {
    setOptions((prev) => prev.map((opt) => (opt.id === id ? { ...opt, label: { text: value } } : opt)));
  };

  const handlePointsChange = (id, value) => {
    setOptions((prev) => prev.map((opt) => (opt.id === id ? { ...opt, points: value } : opt)));
  };

  const handleCorrectChange = (id) => {
    setOptions((prev) => prev.map((opt) => ({ ...opt, isCorrect: opt.id === id })));
  };

  const addOption = () => {
    setOptions((prev) => [...prev, { id: crypto.randomUUID(), label: { text: "" }, points: "0", isCorrect: false }]);
  };

  const removeOption = (id) => {
    setOptions((prev) => prev.filter((opt) => opt.id !== id));
  };

  return (
    <>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(true);

          const areOptionsFilled = options.every((opt) => opt.label.text.trim());
          const arePointsFilled = options.every((opt) => opt.points !== "" && opt.points !== null && opt.points !== undefined);

          const hasCorrectAnswer = options.some((opt) => opt.isCorrect);

          if (!areOptionsFilled || !hasCorrectAnswer || !arePointsFilled) {
            return;
          }

          handleSubmit(event, (formValues) => {
            mutate({ ...formValues, options });
          });
        }}
      >
        <div className="flex justify-between items-center w-full px-6">
          <div className="font-normal text-lg xl:text-3xl text-primary">Manage Spin Wheel</div>
          <div className="flex gap-4">
            <Button
              buttonType="submit"
              buttonLabel="Save"
              buttonClasses="min-w-28"
              buttonLabelClasses="mx-auto"
              {...(isLoading && {
                loading: true,
                buttonIconPosition: "left",
                isDisable: true,
              })}
            />
            <Button
              buttonLabel="Cancel"
              buttonClasses="min-w-28 !bg-white !text-primary border !border-primary"
              buttonLabelClasses="mx-auto"
              buttonHasLink={true}
              buttonLink={"/admin/spin-wheel"}
            />
          </div>
        </div>

        <div className="grid grid-cols-4 gap-6 p-6">
          <div className="col-span-3 space-y-4">
            <Input
              label="Title"
              inputPlaceholder="Enter title"
              onChange={(e) => handleChange({ target: { name: "title", value: e.target.value } })}
              inputName={"title"}
              required={false}
              value={values?.title || ""}
              {...formValidate(errors, "title")}
            />

            <div className="text-xs font-medium text-text-disabled font-CanelaDeck">
              Wheel Details
              <span className="text-[8px] text-red-500 ml-1 relative top-[-2px]">
                <i className="fa-solid fa-asterisk"></i>
              </span>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
              <div className="grid grid-cols-4 gap-4 px-2 text-xs font-semibold text-gray-600 border-b pb-2">
                <div>Wheel Start</div>
                <div>Display Text</div>
                <div>Points</div>
                <div></div>
              </div>

              {options.map((option, index) => (
                <div
                  key={option.id}
                  className="grid grid-cols-4 gap-4 items-center p-4 border rounded-lg shadow-sm bg-gray-50 hover:shadow-md transition"
                >
                  <label className="flex items-center justify-start cursor-pointer">
                    <input
                      type="radio"
                      name="correctAnswer"
                      checked={option.isCorrect}
                      onChange={() => handleCorrectChange(option.id)}
                      className="hidden peer"
                    />
                    <span className="w-6 h-6 flex items-center justify-center border-2 border-primary rounded-md peer-checked:bg-primary">
                      <Check className="w-4 h-4 text-white" />
                    </span>
                  </label>

                  <input
                    type="text"
                    placeholder={`display text ${index + 1}`}
                    value={option?.label?.text}
                    onChange={(e) => handleOptionChange(option.id, e.target.value)}
                    className={`w-full border rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-pink-400 ${
                      submitted && (option.points === "" || option.points === null || option.points === undefined)
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  />

                  <input
                    type="number"
                    placeholder={`add points`}
                    value={option.points}
                    onChange={(e) => handlePointsChange(option.id, e.target.value)}
                    className={`w-full border rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-pink-400 ${
                      submitted && (option.points === "" || option.points === null || option.points === undefined)
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  />

                  {options.length > 4 && (
                    <button type="button" onClick={() => removeOption(option.id)} className="text-red-500 hover:text-red-700 text-sm font-bold">
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                buttonLabel="+ Add Segments"
                buttonClasses="mt-3 w-[160px] !h-[40px] 
    bg-transparent 
    font-semibold rounded-lg 
    text-transparent bg-clip-text 
    bg-gradient-to-r from-fuchsia-500 to-purple-600 
    hover:opacity-90"
                buttonFunction={addOption}
              />
            </div>
            {submitted && !options.every((opt) => opt.label.text.trim() && opt.points !== "" && opt.points !== null && opt.points !== undefined) && (
              <div className="text-xs text-amber-600 mt-1">
                <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
                All Segments & points are required
              </div>
            )}

            {submitted &&
              options.every((opt) => opt.label.text.trim() && opt.points !== "" && opt.points !== null && opt.points !== undefined) &&
              !options.some((opt) => opt.isCorrect) && (
                <div className="text-xs text-amber-600 mt-1">
                  <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
                  Please select a wheel start head
                </div>
              )}

            <div className="relative col-span-4 mt-5">
              <div className="flex gap-4">
                <Button
                  buttonType="submit"
                  buttonLabel={"Save"}
                  buttonClasses={"min-w-28"}
                  buttonLabelClasses={"mx-auto"}
                  {...(isLoading && {
                    loading: true,
                    buttonIconPosition: "left",
                    isDisable: true,
                  })}
                />
                <Button
                  buttonLabel={"Cancel"}
                  buttonClasses={"min-w-28 !bg-white !text-primary border !border-primary"}
                  buttonLabelClasses={"mx-auto"}
                  buttonHasLink={true}
                  buttonLink={"/admin/spin-wheel"}
                />
              </div>
            </div>
          </div>

          <div className="col-span-1 mt-5 space-y-6">
            <div className="border border-gray-300 rounded-lg p-4 space-y-2">
              <DatePicker
                label={"Publish Start Date"}
                placeholder={"Start date"}
                position="fixed"
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "publishStartDate",
                      value: e?.startDate || e?.endDate,
                    },
                  })
                }
                value={{
                  startDate: values?.publishStartDate,
                  endDate: values?.publishStartDate,
                }}
              />
              <DatePicker
                label={"Publish End Date"}
                placeholder={"End date"}
                position="fixed"
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "publishEndDate",
                      value: e?.startDate || e?.endDate,
                    },
                  })
                }
                value={{
                  startDate: values?.publishEndDate,
                  endDate: values?.publishEndDate,
                }}
              />
              <FlexibleSelect
                label={"Status"}
                placeholder={"Choose status"}
                onChange={(e) => handleChange({ target: { name: "status", value: e } })}
                selectedValue={values?.status}
                dropdownData={[
                  { name: "Active", value: "active" },
                  { name: "Inactive", value: "inactive" },
                ]}
                {...formValidate(errors, "status")}
              />
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default ManageSpinWheel;
